import { lookupsJson, resolveLookupOptions } from '@ourbranch/lookups';

import { appConfig, brandName } from 'brand-config';
import { isAutoDirectBillAllowed } from 'core/helpers/auto-direct-bill';

/**
 * Auto interested parties, brand-typed (#36264).
 *
 * CFM distinguishes the interests its filed forms and claims handling need — Additional Insured -
 * Lessor (PA 1100), Loss Payee, Trustee (PA 1300) and Additional Interest — and calls none of them a
 * "Lien Holder". One party can attach to several vehicles, and a Trustee party carries the trust as
 * its name plus the trustee name(s) the PA 1300 schedule prints. Everything here is gated on
 * `featureFlags.autoInterestedPartyTypes`; without it Branch keeps its legacy list, labels and
 * single-vehicle mapping exactly as before.
 */
export const usesBrandInterestedPartyTypes = () => Boolean(appConfig?.featureFlags?.autoInterestedPartyTypes);

/**
 * The payer of a direct-billed auto policy (#36200). It holds no interest in a vehicle: it carries the
 * email invoices go to instead of vehicles, a loan number or a loss-payee flag.
 */
export const BILL_TO_OTHER_RELATIONSHIP = 'billToOther';

/** Relationships whose party is a loss payee on the vehicle (prints as such, gets a loan number). */
const LOSS_PAYEE_RELATIONSHIPS = ['lienholder', 'lossPayee', 'lessor'];
export const isLossPayeeRelationship = (relationship) => LOSS_PAYEE_RELATIONSHIPS.includes(relationship);

export const interestedPartyListLabels = () =>
  usesBrandInterestedPartyTypes()
    ? {
        title: 'Interested Party',
        addLabel: 'Add Interested Party',
        singular: 'Interested Party',
        plural: 'Interested Parties'
      }
    : { title: 'Interested Party or Lienholder', addLabel: 'Add Lien Holder', singular: 'Entity', plural: 'Entities' };

/**
 * The Relationship options. A brand with its own typed list gets it (it has no renters or lienholder
 * entries to filter), less Bill to other unless the brand direct-bills auto and no other party on the
 * offer/policy is already the payer (#36200); otherwise the legacy state/renters filtering of the base
 * list.
 */
export const interestedPartyRelationshipOptions = ({
  state,
  showRenters = false,
  standaloneRenters = false,
  existingParties = []
}) => {
  if (usesBrandInterestedPartyTypes()) {
    const allowBillToOther =
      isAutoDirectBillAllowed() && !existingParties.some((party) => party?.relationship === BILL_TO_OTHER_RELATIONSHIP);
    return resolveLookupOptions('autoAdditionalPartyRelationship', state, brandName).filter(
      (option) => allowBillToOther || option.id !== BILL_TO_OTHER_RELATIONSHIP
    );
  }
  const base = lookupsJson.autoAdditionalPartyRelationship;
  if (state === 'MA') {
    return base;
  }
  if (standaloneRenters) {
    return base.filter((item) => item.id === 'renters');
  }
  if (showRenters) {
    // handles auto + renters endorsement
    return base.filter((item) => item.id === 'renters' || item.id === 'lienholder');
  }
  // just lienholder
  return base.filter((item) => item.id !== 'lessor' && item.id !== 'renters');
};

/** The add form's values → the stored party. */
export const buildInterestedPartyFromForm = ({
  id,
  name,
  loanNumber,
  address,
  address2,
  city,
  state,
  zip,
  VIN,
  VINs,
  relationship,
  trusteeNames,
  email
}) => {
  const storedAddress = { address, address2, city, state, zip };
  if (!usesBrandInterestedPartyTypes()) {
    return {
      id,
      name,
      loanNumber,
      address: storedAddress,
      VIN,
      lossPayee: relationship === 'lienholder' || relationship === 'lessor',
      relationship
    };
  }
  if (relationship === BILL_TO_OTHER_RELATIONSHIP) {
    return {
      id,
      name,
      email,
      loanNumber: '',
      address: storedAddress,
      VIN: '',
      VINs: [],
      lossPayee: false,
      relationship,
      trusteeNames: ''
    };
  }
  const vins = (VINs ?? []).filter(Boolean);
  const lossPayee = isLossPayeeRelationship(relationship);
  return {
    id,
    name,
    loanNumber: lossPayee ? loanNumber : '',
    address: storedAddress,
    // VIN stays the first vehicle for the code paths that still read a single one (docs, add-car).
    VIN: vins[0] ?? '',
    VINs: vins,
    lossPayee,
    relationship,
    trusteeNames: relationship === 'trustee' ? trusteeNames : ''
  };
};

/** Stored parties → form values: a multi-vehicle list seeded from the single VIN when absent. */
const vehiclesOf = (party) => {
  if (party?.VINs?.length) {
    return party.VINs;
  }
  return party?.VIN ? [party.VIN] : [];
};

export const hydrateInterestedPartiesForForm = (parties) =>
  (parties ?? []).map((party) => ({
    ...party,
    VINs: vehiclesOf(party),
    trusteeNames: party?.trusteeNames ?? ''
  }));
